import React from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import type { WorkspacePermissionsProviderQuery } from './__generated__/WorkspacePermissionsProviderQuery.graphql';

enum WorkspacePermission {
	manageAutomation = 'workspace/manage-automation',
	manageApp = 'workspace/manage-app',
}

type Permissions = Record<keyof typeof WorkspacePermission, boolean>;

const getBulkPermittedVariables = (
	workspaceId: string,
	spaceId: string,
	accountId: string,
): WorkspacePermissionsProviderQuery['variables'] => {
	const principalId = `ari:cloud:identity::user/${accountId}`;
	const resourceId = `ari:cloud:studio::space/${workspaceId}/${spaceId}`;
	const permissionIds = [WorkspacePermission.manageApp, WorkspacePermission.manageAutomation];
	return {
		principalIds: [principalId, principalId],
		resourceIds: [resourceId, resourceId],
		permissionIds,
		dontRequirePrincipalsInSite: [true, true],
		skip: !workspaceId || !spaceId || !accountId,
	};
};

const isPermissionPermitted = (
	permittedResults: WorkspacePermissionsProviderQuery['response']['bulkPermitted'],
	permissionId: WorkspacePermission,
) =>
	permittedResults?.some(
		(result) => result?.permissionId === permissionId && result?.permitted === true,
	) ?? false;

type WorkspacePermissionsProviderProps = {
	workspaceId: string;
	spaceId: string;
	accountId?: string;
	children: (args: { permissions: Permissions }) => React.JSX.Element;
};

export const WorkspacePermissionsProvider = ({
	workspaceId,
	spaceId,
	accountId,
	children,
}: WorkspacePermissionsProviderProps): React.JSX.Element => {
	const data = useLazyLoadQuery<WorkspacePermissionsProviderQuery>(
		graphql`
			query WorkspacePermissionsProviderQuery(
				$principalIds: [String]
				$resourceIds: [String]
				$permissionIds: [String]
				$dontRequirePrincipalsInSite: [Boolean]
				$skip: Boolean!
			) {
				bulkPermitted(
					principalIds: $principalIds
					resourceIds: $resourceIds
					permissionIds: $permissionIds
					dontRequirePrincipalsInSite: $dontRequirePrincipalsInSite
				) @skip(if: $skip) {
					permissionId
					permitted
				}
			}
		`,
		getBulkPermittedVariables(workspaceId, spaceId, accountId ?? ''),
	);

	const permissions: Permissions = {
		manageAutomation: isPermissionPermitted(
			data.bulkPermitted,
			WorkspacePermission.manageAutomation,
		),
		manageApp: isPermissionPermitted(data.bulkPermitted, WorkspacePermission.manageApp),
	};

	return children({ permissions });
};
